/**
 * Shared helper for bulk-import CSV rows that reference a physical
 * evidence file (a photo, a document) by name in a companion .zip.
 *
 * Same pattern as the certificate bulk-import flow (CertificateList.tsx /
 * BulkCertImportModal.tsx, June 2026): the CSV names the file it wants
 * attached, the file is found in the zip by that name, uploaded to S3 via
 * a presigned URL, SHA-256 hashed, and the real file reference is added
 * to the row before it's sent to the backend. Without this, a "bulk
 * upload" that accepts a zip but never processes it silently drops every
 * document — the CSV import succeeds, but no evidence is actually saved.
 */
import { evidenceService } from '../services/api';

export interface EvidenceAttachResult {
    s3Key?: string;
    fileName?: string;
    contentHash?: string;
    _attachStatus?: 'attached' | 'no-file-in-zip' | 'no-property' | 'upload-failed';
}

const sha256 = async (buf: ArrayBuffer): Promise<string> => {
    const h = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(h)).map((b) => b.toString(16).padStart(2, '0')).join('');
};

const inferType = (fileName: string, fallback: string) => {
    const lower = fileName.toLowerCase();
    if (lower.endsWith('.pdf')) return 'application/pdf';
    if (lower.match(/\.(jpe?g)$/)) return 'image/jpeg';
    if (lower.endsWith('.png')) return 'image/png';
    return fallback || 'application/octet-stream';
};

/**
 * For every row with a value in `filenameField`, find the matching file in
 * `zipFile` (case-insensitive, by base filename), upload it, and return the
 * rows enriched with s3Key/fileName/contentHash. Rows without a filename
 * value, or with no zip at all, pass through unchanged.
 */
export async function attachEvidenceFromZip<T extends Record<string, any>>(
    rows: T[],
    zipFile: File | null | undefined,
    opts: {
        filenameField: string;
        resolvePropertyId: (row: T) => string | null | undefined;
        onProgress?: (message: string) => void;
    },
): Promise<(T & EvidenceAttachResult)[]> {
    if (!zipFile) return rows;

    opts.onProgress?.('Reading zip file…');
    const JSZip = (await import('jszip')).default;
    const zip = await JSZip.loadAsync(zipFile);
    const index: Record<string, any> = {};
    zip.forEach((path: string, entry: any) => {
        if (!entry.dir) {
            const base = path.split('/').pop() || path;
            index[base.toLowerCase()] = entry;
        }
    });

    const enriched: (T & EvidenceAttachResult)[] = [];
    let uploadedCount = 0;
    for (const row of rows) {
        const wanted = String(row[opts.filenameField] || '').trim();
        if (!wanted) { enriched.push(row); continue; }

        const entry = index[wanted.toLowerCase()];
        if (!entry) { enriched.push({ ...row, _attachStatus: 'no-file-in-zip' }); continue; }

        const propertyId = opts.resolvePropertyId(row);
        if (!propertyId) { enriched.push({ ...row, _attachStatus: 'no-property' }); continue; }

        try {
            opts.onProgress?.(`Uploading ${wanted} (${uploadedCount + 1})…`);
            const blob = await entry.async('blob');
            const fileType = inferType(wanted, blob.type);
            const file = new File([blob], wanted, { type: fileType });

            const pres = await evidenceService.getPresignedUrl({ fileName: file.name, fileType: file.type, propertyId });
            const { presignedUrl, s3Key } = pres.data?.data || {};
            if (!presignedUrl || !s3Key) throw new Error('no presigned URL');

            const ab = await file.arrayBuffer();
            const putRes = await fetch(presignedUrl, { method: 'PUT', body: ab, headers: { 'Content-Type': file.type } });
            if (!putRes.ok) throw new Error('S3 PUT ' + putRes.status);

            const contentHash = await sha256(ab);
            enriched.push({ ...row, s3Key, fileName: file.name, contentHash, _attachStatus: 'attached' });
            uploadedCount++;
        } catch (err: any) {
            console.warn('[BulkEvidence] upload failed for', wanted, err?.message);
            enriched.push({ ...row, _attachStatus: 'upload-failed' });
        }
    }
    opts.onProgress?.('');
    return enriched;
}
